import React from 'react';
import { Phone, MessageCircle, MapPin, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { mockCompanyInfo } from '../mock';

export const Contact = () => {
  const openWhatsApp = () => {
    const message = encodeURIComponent("Hello! I'm interested in La-Klin's security and cleaning services. Please provide more information.");
    window.open(`https://wa.me/${mockCompanyInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
  };

  const openLiveChat = () => {
    // This will be replaced with actual live chat integration
    alert('Live chat will open here. Please use WhatsApp for now or call us directly.');
  };

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Get In <span className="text-red-600">Touch</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to secure and maintain your property with professional excellence? 
            Contact us today for immediate assistance or to schedule a consultation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Phone */}
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Call Us</p>
                    <a 
                      href={`tel:${mockCompanyInfo.phone}`}
                      className="text-red-600 hover:text-red-700 font-medium transition-colors duration-200"
                    >
                      {mockCompanyInfo.phone}
                    </a>
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">WhatsApp</p>
                    <Button
                      onClick={openWhatsApp}
                      className="text-red-600 hover:text-red-700 font-medium transition-colors duration-200 p-0 h-auto bg-transparent hover:bg-transparent"
                    >
                      {mockCompanyInfo.whatsapp}
                    </Button>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Visit Us</p>
                    <p className="text-gray-600">{mockCompanyInfo.address}</p>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Availability</p>
                    <p className="text-gray-600">{mockCompanyInfo.hours}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Service Areas */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-gray-900">Service Areas</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  We proudly serve clients across multiple provinces:
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {mockCompanyInfo.provinces.map((province) => (
                    <div 
                      key={province}
                      className="bg-gray-100 px-3 py-2 rounded-lg text-sm text-gray-700 font-medium"
                    >
                      {province}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="space-y-8">
            {/* Emergency Contact */}
            <Card className="bg-red-50 border-2 border-red-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-red-800 mb-2">Emergency Services</h3>
                <p className="text-red-700 mb-4">
                  For urgent security incidents or emergency cleaning needs, call our 24/7 hotline:
                </p>
                <a 
                  href={`tel:${mockCompanyInfo.phone}`}
                  className="text-2xl font-bold text-red-600 hover:text-red-700 transition-colors duration-200"
                >
                  {mockCompanyInfo.phone}
                </a>
              </CardContent>
            </Card>

            {/* Contact Options */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900">Get In Touch</CardTitle>
                <p className="text-gray-600">
                  Choose your preferred way to contact us for quotes and inquiries.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button 
                  onClick={openWhatsApp}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 text-lg font-semibold"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Chat on WhatsApp
                </Button>
                
                <Button 
                  onClick={openLiveChat}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg font-semibold"
                >
                  💬 Start Live Chat
                </Button>
                
                <Button 
                  onClick={() => document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' })}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3 text-lg font-semibold"
                >
                  Get Free Quote
                </Button>
                
                <div className="text-center pt-4">
                  <p className="text-gray-600 mb-2">Or call us directly:</p>
                  <a 
                    href={`tel:${mockCompanyInfo.phone}`}
                    className="text-xl font-bold text-red-600 hover:text-red-700 transition-colors duration-200"
                  >
                    {mockCompanyInfo.phone}
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};